import React, {useEffect, useMemo, useState} from "react";
import * as Sentry from '@sentry/browser';
import {withAuth} from "../../hoc/Auth";
import Api from "../../Api";
import {FormattedMessage} from "react-intl";
import Chart from "react-google-charts";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import classes from "./styles.module.css";

const STUCK_MINUTES = 30;

const isCreatedType = (t) => t === "created" || /Created$/i.test(t);
const isPendingType = (t) => /Pending$/i.test(t);

const minutesDiffFromNow = (iso) => {
    const d = Date.parse(iso);
    if (Number.isNaN(d)) return Infinity;
    return (Date.now() - d) / 60000;
};

function formatNumber(num) {
    return new Intl.NumberFormat("ru-RU").format(Math.round(num));
}

function getProgressColor(percent) {
    if (percent >= 100) return "#f44336";
    if (percent >= 90) return "#ff5722";
    if (percent >= 75) return "#ff9800";
    return "#4caf50";
}

const Console = ({token}) => {
    var _txState = useState([]);
    var tx = _txState[0];
    var setTx = _txState[1];
    var _loadingState = useState(true);
    var loading = _loadingState[0];
    var setLoading = _loadingState[1];
    var _errState = useState(null);
    var loadErr = _errState[0];
    var setLoadErr = _errState[1];

    var _clData = useState(null);
    var clData = _clData[0];
    var setClData = _clData[1];
    var _clLoading = useState(true);
    var clLoading = _clLoading[0];
    var setClLoading = _clLoading[1];
    var _clError = useState(null);
    var clError = _clError[0];
    var setClError = _clError[1];

    useEffect(function () {
        var mounted = true;
        (function load() {
            setLoading(true);
            setLoadErr(null);
            var filters = {
                filters: [
                    {purchasePending: true}, {payoutPending: true}, {refundPending: true},
                    {reversalPending: true}, {recurrentPending: true}, {sbpPending: true},
                    {mP2PPending: true}, {qrPending: true}, {mobilePayoutPending: true},
                    {threeDSPending: true},
                    {purchaseCreated: true}, {payoutCreated: true}, {refundCreated: true},
                    {reversalCreated: true}, {recurrentCreated: true}, {sbpCreated: true},
                    {mP2PCreated: true}, {qrCreated: true}, {mobilePayoutCreated: true},
                    {created: true},
                ],
                page: 1,
                itemsPerPage: 10000,
            };

            new Api({token}).setTransactionsFilters(filters).then(function (data) {
                if (!mounted) return;
                var arr = data && Array.isArray(data.data) ? data.data : [];
                setTx(arr);
                setLoading(false);
            }).catch(function (e) {
                Sentry.captureException(e);
                if (mounted) {
                    setLoadErr("Не удалось загрузить данные");
                    setLoading(false);
                }
                console.error(e);
            });
        })();
        return function () { mounted = false; };
    }, [token]);

    useEffect(function () {
        var cancelled = false;

        function loadCraftlite() {
            setClLoading(true);
            new Api({token}).getCraftliteLimitStats().then(function (result) {
                if (!cancelled) {
                    setClData(result);
                    setClError(null);
                    setClLoading(false);
                }
            }).catch(function (err) {
                Sentry.captureException(err);
                if (!cancelled) {
                    setClError(err.message || "Failed to load data");
                    setClLoading(false);
                }
            });
        }

        loadCraftlite();
        var interval = setInterval(loadCraftlite, 30000);
        return function () {
            cancelled = true;
            clearInterval(interval);
        };
    }, [token]);

    var counts = useMemo(function () {
        var pending = 0;
        var created = 0;
        var stuck = 0;

        for (var i = 0; i < tx.length; i++) {
            var t = tx[i];
            var type = String((t && t.type) || "").trim();
            var isP = isPendingType(type);
            var isC = isCreatedType(type);
            if (!isP && !isC) continue;

            var ageMin = minutesDiffFromNow(t && t.date);
            var isStuck = ageMin >= STUCK_MINUTES;

            if (isStuck) {
                stuck++;
            } else if (isP) {
                pending++;
            } else if (isC) {
                created++;
            }
        }

        return {pending: pending, created: created, error: stuck};
    }, [tx]);

    var pieData = clData
        ? [
            ["Segment", "Amount"],
            ["Использовано", clData.usedRub],
            ["Остаток", clData.remainingRub],
        ]
        : null;

    var barData =
        clData && clData.merchants && clData.merchants.length > 0
            ? [
                ["Мерчант", "Сумма (RUB)", {role: "style"}],
            ].concat(clData.merchants.map(function (m, i) {
                var colors = ["#2196f3", "#4caf50", "#ff9800", "#9c27b0", "#f44336", "#00bcd4"];
                return [m.merchantName, m.sumRub, colors[i % colors.length]];
            }))
            : null;

    return (
        <Wrapper>
            <Container style={{justifyContent: "center"}}>
                <Column width={1180}>
                    <span className={classes.Console}>
                        <FormattedMessage id="Console.title" defaultMessage="Админ-панель"/>
                    </span>

                        <div className={classes.AlarmBlock}>
                            <h4>
                                <FormattedMessage id="Console.transactionsTitle"
                                                  defaultMessage="Транзакции требующие внимания"/>
                            </h4>

                            {loading ? (
                                <p><FormattedMessage id="Console.loading" defaultMessage="Загрузка…"/></p>
                            ) : loadErr ? (
                                <p className={classes.ErrorText}>{loadErr}</p>
                            ) : (
                                <table className={classes.SimpleTable}>
                                    <thead>
                                    <tr>
                                        <th><FormattedMessage id="Console.pending" defaultMessage="Pending"/></th>
                                        <th><FormattedMessage id="Console.created" defaultMessage="Created"/></th>
                                        <th><FormattedMessage id="Console.error" defaultMessage="Error"/></th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr>
                                        <td><strong>{counts.pending}</strong></td>
                                        <td><strong>{counts.created}</strong></td>
                                        <td><strong>{counts.error}</strong></td>
                                    </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Craftlite Limit */}
                        <div className={classes.AlarmBlock}>
                            <h4>Дневной лимит Craftlite</h4>

                            {clLoading && !clData ? (
                                <div className={classes.ClLoadingWrap}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : clError ? (
                                <div className={classes.ClErrorCard}>{clError}</div>
                            ) : (
                                <div className={`row ${classes.ClRow}`}>
                                    <div className={`col-md-8 ${classes.ClCol}`}>
                                        <div className={classes.ClCard}>
                                            <div className={classes.ClCardHeader}>
                                                <h5 className={classes.ClCardTitle}>Общий лимит</h5>
                                                {clData && <span className={classes.ClDateLabel}>{clData.date}</span>}
                                            </div>
                                            <div className={classes.ClCardBody}>
                                                {pieData && (
                                                    <div className={classes.ClChartWrap}>
                                                        <Chart
                                                            width="100%"
                                                            height="300px"
                                                            chartType="PieChart"
                                                            data={pieData}
                                                            options={{
                                                                pieHole: 0.5,
                                                                colors: [getProgressColor(clData.usedPercent), "#e0e0e0"],
                                                                legend: {position: "bottom", textStyle: {fontSize: 13}},
                                                                chartArea: {width: "90%", height: "80%"},
                                                                pieSliceText: "value",
                                                                tooltip: {text: "both"},
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {barData && (
                                            <div className={classes.ClCard}>
                                                <div className={classes.ClCardHeader}>
                                                    <h5 className={classes.ClCardTitle}>По мерчантам</h5>
                                                </div>
                                                <div className={classes.ClCardBody}>
                                                    <div className={classes.ClChartWrap}>
                                                        <Chart
                                                            width="100%"
                                                            height="300px"
                                                            chartType="BarChart"
                                                            data={barData}
                                                            options={{
                                                                legend: "none",
                                                                chartArea: {width: "60%", height: "80%"},
                                                                hAxis: {title: "RUB", minValue: 0},
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {clData && clData.merchants && clData.merchants.length > 0 && (
                                            <div className={classes.ClCard}>
                                                <div className={classes.ClCardHeader}>
                                                    <h5 className={classes.ClCardTitle}>Детализация</h5>
                                                </div>
                                                <div className={classes.ClCardBody}>
                                                    <table className={classes.ClMerchantTable}>
                                                        <thead>
                                                        <tr>
                                                            <th>Мерчант</th>
                                                            <th className={classes.ClAlignRight}>Сумма (RUB)</th>
                                                            <th className={classes.ClAlignRight}>Транзакции</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {clData.merchants.map(function (m) {
                                                            return (
                                                                <tr key={m.merchantId}>
                                                                    <td>{m.merchantName}</td>
                                                                    <td className={classes.ClAlignRight}>{formatNumber(m.sumRub)}</td>
                                                                    <td className={classes.ClAlignRight}>{m.count}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className={`col-md-4 ${classes.ClCol}`}>
                                        {clData && (
                                            <div>
                                                <div className={classes.ClSummaryCard}>
                                                    <h6 className={classes.ClSummaryTitle}>Общий лимит</h6>

                                                    <div className={classes.ClProgressWrap}>
                                                        <div className={classes.ClProgressBar}>
                                                            <div
                                                                className={classes.ClProgressFill}
                                                                style={{
                                                                    width: Math.min(clData.usedPercent, 100) + "%",
                                                                    backgroundColor: getProgressColor(clData.usedPercent),
                                                                }}
                                                            />
                                                        </div>
                                                        <div className={classes.ClProgressInfo}>
                                                            <span>{clData.usedPercent.toFixed(1)}%</span>
                                                            <span>{clData.date}</span>
                                                        </div>
                                                    </div>

                                                    <div className={classes.ClSummaryRow}>
                                                        <span className={classes.ClSummaryLabel}>Лимит</span>
                                                        <span className={classes.ClSummaryValue}>{formatNumber(clData.limitRub)} RUB</span>
                                                    </div>
                                                    <div className={classes.ClSummaryRow}>
                                                        <span className={classes.ClSummaryLabel}>Использовано</span>
                                                        <span className={classes.ClSummaryValue}>{formatNumber(clData.usedRub)} RUB</span>
                                                    </div>
                                                    <div className={classes.ClSummaryRow}>
                                                        <span className={classes.ClSummaryLabel}>Остаток</span>
                                                        <span className={classes.ClSummaryValue}>{formatNumber(clData.remainingRub)} RUB</span>
                                                    </div>

                                                    {clData.isBlocked ? (
                                                        <div className={classes.ClBlockedBadge}>ЛИМИТ ДОСТИГНУТ</div>
                                                    ) : (
                                                        <div className={classes.ClActiveBadge}>Активен</div>
                                                    )}
                                                </div>

                                                <div className={classes.ClSummaryCard}>
                                                    <h6 className={classes.ClSummaryTitle}>Пороги оповещений</h6>
                                                    <div className={classes.ClSummaryRow}>
                                                        <span className={classes.ClSummaryLabel}>1 000 000 RUB</span>
                                                        <span className={classes.ClSummaryValue}>{clData.usedRub >= 1000000 ? "+" : "-"}</span>
                                                    </div>
                                                    <div className={classes.ClSummaryRow}>
                                                        <span className={classes.ClSummaryLabel}>1 250 000 RUB</span>
                                                        <span className={classes.ClSummaryValue}>{clData.usedRub >= 1250000 ? "+" : "-"}</span>
                                                    </div>
                                                    <div className={classes.ClSummaryRow}>
                                                        <span className={classes.ClSummaryLabel}>1 500 000 RUB</span>
                                                        <span className={classes.ClSummaryValue}>{clData.usedRub >= 1500000 ? "+" : "-"}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                </Column>
            </Container>
        </Wrapper>
    );
};

export default withAuth(Console);



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Console/index.js